import CloseOutlined from '@ant-design/icons/CloseOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import SaveOutlined from '@ant-design/icons/SaveOutlined'
import type { TabsProps } from 'antd'
import { Button, Layout, Modal, Tabs } from 'antd'
import { RolodexDocumentEntry } from 'features/files'
import { useGeckoNotifications } from 'features/notifications'
import { AnimatePresence } from 'framer-motion'
import first from 'lodash/first'
import React, { SetStateAction, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'

import { useDocumentEditContext } from './DocumentEditContext'
import {
  documentFieldTransition,
  documentFieldVariants,
  MotionDocumentSectionFieldsContainer,
} from './fieldEditor/DocumentFieldItemAnimation'
import { DocumentMetadataEditor } from './fieldEditor/DocumentMetadataEditor'
import {
  blankDocumentSectionFields,
  DocumentSectionFields,
} from './fieldEditor/documentSectionFieldsApi'
import {
  DocumentFieldsContentView,
  DocumentSectionFieldsSetter,
  DocumentSectionFieldsView,
} from './fieldEditor/DocumentSectionFieldsView'
import {
  DocumentOntology,
  ROLODEX_DOCUMENT_ONTOLOGY_TYPE,
  useDocumentOntologyAdditions,
} from './fieldEditor/useDocumentOntologyAdditions'

export interface DocumentFieldEditorPanelProps {
  document: RolodexDocumentEntry
  isLoading?: boolean
  onSave?: () => Promise<void>
}

const PanelLayout = styled(Layout)`
  height: 100%;
`

const StyledLayoutContent = styled(Layout.Content)`
  overflow: scroll;
`

const StyledLayoutFooter = styled(Layout.Footer)`
  display: flex !important;
  flex-direction: row-reverse;
  background: ${({ theme }) => theme.forecast.background3};
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.forecast.background3};
  gap: 0.5rem;
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    padding: 0 24px;
  }
`

export const DocumentFieldEditorPanel: React.FC<
  DocumentFieldEditorPanelProps
> = ({ document, onSave, isLoading }) => {
  const {
    documentData,
    sectionData,
    isLoading: contextLoading,
    isSaving,
    doSave,
    hasChanges,
  } = useDocumentEditContext()

  const { miscFields, setMiscFields, reset: resetDocumentData } = documentData
  const {
    documentSectionFields,
    localDocumentSectionFields,
    setLocalDocumentSectionFields,
    deleteDocumentFieldIds,
    setDeleteDocumentFieldIds,
    isLoading: isSectionLoading,
    reset: resetSectionData,
  } = sectionData

  const { documentOntologies, isLoading: isLoadingDocumentOntologies } =
    useDocumentOntologyAdditions(
      useMemo(
        () => ({ organization: document.tags.organization }),
        [document.tags.organization]
      )
    )

  const [isConfirmVisible, setConfirmVisible] = useState(false)

  const computeSectionTitle = useCallback(
    (
      title: string | undefined,
      ontologyId: string | null | undefined
    ): string => {
      if (!title || title.startsWith('Untitled')) {
        const ontology = documentOntologies.find(
          (ontology) => ontology.id === ontologyId
        )
        return `Untitled ${ontology?.name ?? ''} Section`
      }
      return title
    },
    [documentOntologies]
  )

  const createFieldsSetter = useCallback(
    (id: string): DocumentSectionFieldsSetter =>
      (val, options) => {
        setLocalDocumentSectionFields((prev) => {
          const prevFields = prev?.get(id) ??
            documentSectionFields.get(id) ?? {
              ...blankDocumentSectionFields(),
              id,
            }
          const newFields = typeof val === 'function' ? val(prevFields) : val
          const newMap = new Map(prev ?? [])
          if (newFields) {
            const titleSource =
              newFields.title ?? prevFields?.title ?? 'Untitled'
            const ontologyId =
              newFields.documentOntologyId ??
              prevFields?.documentOntologyId ??
              null
            const computedTitle =
              options?.preserveTitle && newFields.title !== undefined
                ? newFields.title
                : computeSectionTitle(titleSource, ontologyId)
            newMap.set(id, { ...newFields, title: computedTitle })
            setDeleteDocumentFieldIds((prevIds) =>
              prevIds.filter((existingId) => existingId !== id)
            )
          } else {
            newMap.delete(id)
            setDeleteDocumentFieldIds((prevIds) => [...prevIds, id])
          }
          return newMap
        })
      },
    [
      computeSectionTitle,
      documentSectionFields,
      setDeleteDocumentFieldIds,
      setLocalDocumentSectionFields,
    ]
  )

  const addDocumentSectionFields = useCallback(() => {
    setLocalDocumentSectionFields((prev) => {
      const newMap = new Map(prev ?? [])
      const newId = `fields-${document.entry_id}-${v4().substring(0, 6)}`
      const ontologyId = first(documentOntologies)?.id
      const baseFields = {
        ...blankDocumentSectionFields(),
        id: newId,
        documentOntologyId: ontologyId,
        tags: {
          [document.type]: document.entry_id,
          [ROLODEX_DOCUMENT_ONTOLOGY_TYPE]: ontologyId,
          ...document.tags,
        },
      }
      const computedTitle = computeSectionTitle(baseFields.title, ontologyId)
      newMap.set(newId, { ...baseFields, title: computedTitle })
      setDeleteDocumentFieldIds((prevIds) =>
        prevIds.filter((deleteId) => deleteId !== newId)
      )
      return newMap
    })
  }, [
    computeSectionTitle,
    document.entry_id,
    document.tags,
    document.type,
    documentOntologies,
    setDeleteDocumentFieldIds,
    setLocalDocumentSectionFields,
  ])

  const sortedDocumentSectionFields = useMemo(() => {
    const allIds = new Set<string>(
      [
        ...(localDocumentSectionFields?.keys() ?? []),
        ...(documentSectionFields?.keys() ?? []),
      ].filter((id) => !deleteDocumentFieldIds.includes(id))
    )
    return sortDocumentSectionFields(
      Array.from(allIds)
        .map(
          (id) =>
            localDocumentSectionFields?.get(id) ??
            documentSectionFields?.get(id) ?? {
              ...blankDocumentSectionFields(),
              id,
            }
        )
        .toSorted((a, b) => {
          return a.createdAt.localeCompare(b.createdAt)
        })
    )
  }, [
    deleteDocumentFieldIds,
    documentSectionFields,
    localDocumentSectionFields,
  ])

  const { errorNotification } = useGeckoNotifications()
  const openConfirmModal = useCallback(() => setConfirmVisible(true), [])
  const closeConfirmModal = useCallback(() => setConfirmVisible(false), [])

  const handleSave = useCallback(async () => {
    try {
      await doSave()
      await onSave?.()
    } catch (error) {
      errorNotification(`Failed to save fields: ${String(error)}`)
    } finally {
      closeConfirmModal()
    }
  }, [closeConfirmModal, doSave, errorNotification, onSave])

  const handleCancel = useCallback(() => {
    resetSectionData()
    resetDocumentData()
  }, [resetDocumentData, resetSectionData])

  const loading =
    isLoading ||
    contextLoading ||
    isSaving ||
    isLoadingDocumentOntologies ||
    isSectionLoading

  const tabItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'ontology-fields',
        label: 'Ontology Field Sections',
        children: (
          <>
            <DocumentSectionFieldsList
              documentSections={sortedDocumentSectionFields}
              isLoading={isSectionLoading}
              createFieldsSetter={createFieldsSetter}
              documentOntologies={documentOntologies}
              documentId={document.entry_id}
            />
            <AddFieldSectionButton
              onClick={addDocumentSectionFields}
              icon={<PlusOutlined />}>
              Add Field Section
            </AddFieldSectionButton>
          </>
        ),
      },
      {
        key: 'misc-fields',
        label: 'Additional Fields',
        children: (
          <DocumentFieldsContentView
            fields={miscFields as Record<string, unknown>}
            selectedDocumentOntology={undefined}
            setFields={setMiscFields}
            documentId={document.entry_id}
          />
        ),
      },
      {
        key: 'notes',
        label: 'Metadata',
        children: <DocumentMetadataEditor />,
      },
    ],
    [
      addDocumentSectionFields,
      createFieldsSetter,
      document.entry_id,
      documentOntologies,
      isSectionLoading,
      miscFields,
      setMiscFields,
      sortedDocumentSectionFields,
    ]
  )

  return (
    <PanelLayout>
      <StyledLayoutContent>
        <StyledTabs
          items={tabItems}
          defaultActiveKey='ontology-fields'
          animated={{ inkBar: true, tabPane: true }}
        />
      </StyledLayoutContent>
      <StyledLayoutFooter>
        <SaveButton
          onClick={openConfirmModal}
          loading={loading}
          disabled={!hasChanges}
          icon={<SaveOutlined />}>
          Save
        </SaveButton>
        <CancelButton
          onClick={handleCancel}
          icon={<CloseOutlined />}
          disabled={!hasChanges}>
          Cancel
        </CancelButton>
      </StyledLayoutFooter>
      <Modal
        open={isConfirmVisible}
        onOk={handleSave}
        confirmLoading={isSaving}
        onCancel={closeConfirmModal}
        okText='Save'
        cancelText='Cancel'
        title='Save changes?'>
        Are you sure you want to save your document field changes?
      </Modal>
    </PanelLayout>
  )
}

type DocumentSectionFieldsSetterFactory = (
  id: string
) => DocumentSectionFieldsSetter

interface DocumentSectionFieldsListProps {
  documentSections: ReadonlyArray<DocumentSectionFields>
  isLoading: boolean
  createFieldsSetter: DocumentSectionFieldsSetterFactory
  documentOntologies: ReadonlyArray<DocumentOntology>
  documentId: string
}

const DocumentSectionFieldsList: React.FC<DocumentSectionFieldsListProps> = ({
  documentSections,
  isLoading,
  createFieldsSetter,
  documentOntologies,
  documentId,
}) => {
  const fieldSettersMap = useMemo<
    ReadonlyMap<
      string,
      (value: SetStateAction<DocumentSectionFields | undefined>) => void
    >
  >(() => {
    return new Map(
      documentSections.map((docFields) => [
        docFields.id,
        createFieldsSetter(docFields.id),
      ])
    )
  }, [documentSections, createFieldsSetter])

  return (
    <div>
      <AnimatePresence initial={false}>
        {documentSections.map((docFields) => (
          <MotionDocumentSectionFieldsContainer
            key={docFields.id}
            variants={documentFieldVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            transition={documentFieldTransition}
            layout>
            <DocumentSectionFieldsView
              isLoading={isLoading}
              documentSectionFields={docFields}
              setDocumentSectionFields={fieldSettersMap.get(docFields.id)!}
              documentOntologies={documentOntologies}
              documentId={documentId}
            />
          </MotionDocumentSectionFieldsContainer>
        ))}
      </AnimatePresence>
    </div>
  )
}

function sortDocumentSectionFields(
  documentSectionFields: ReadonlyArray<DocumentSectionFields> | undefined
): ReadonlyArray<DocumentSectionFields> {
  return (
    documentSectionFields?.toSorted((a, b) => {
      return a.createdAt.localeCompare(b.createdAt)
    }) ?? []
  )
}

const SaveButton = styled(Button)`
  background: ${({ theme }) => theme.forecast.background2};
  color: ${({ theme }) => theme.forecast.activeText};
  background-color: ${({ theme }) => theme.forecast.primary};
`

const CancelButton = styled(Button)`
  background: ${({ theme }) => theme.forecast.background2};
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.activeText};
`

const AddFieldSectionButton = styled(Button)`
  display: block;
  margin: 24px auto;
  background: ${({ theme }) => theme.forecast.background3};
  border-color: ${({ theme }) => theme.forecast.background3};
  color: ${({ theme }) => theme.forecast.activeText};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.background3};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`
